import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { AlertTriangle, Calendar, FileSignature, Inbox, Users } from 'lucide-react'
import { Button, Input, PipelineBadge, isPipelineStage } from '@agms/ui'
import { useAuth, usePermissions } from '../hooks/useAuth'
import { PIPELINE_STAGES, useLeads } from '../hooks/useLeads'
import api, { appointmentsApi } from '../lib/api'
import type { Lead } from '../lib/api'
import { MetricBars, pipelineBars, portfolioBars } from '../components/dashboard/MetricBars'
import { KnownIssueBanner } from '../components/help/KnownIssueBanner'
import { formatCurrency, formatSource } from '../lib/format'
import { APPOINTMENT_NAV_ROLES } from '../lib/navAccess'
import { CardTitle, MiniStat, PageHeader, StatCard, SurfaceCard } from '../components/ui'
import type { MiniStatTone } from '../components/ui'

interface DashboardMetrics {
  lead_counts: {
    total: number
    new_business: number
    esign: number
    verification: number
    underwriting: number
    build: number
    live: number
    client_returned: number
    conditional_approval: number
    pended: number
    validifi_flagged: number
  }
  portfolio: {
    active_merchants: number
    mtd_volume: number
    ytd_volume: number
    mtd_transaction_count: number
    mtd_refunds: number
    open_chargebacks: number
    mtd_chargeback_amount: number
    chargeback_ratio: number
    no_batch_7_days: number
    first_batches_this_week: number
    sync_stale: number
    sync_failed: number
  } | null
  top_merchants: Array<{
    id: string
    dba_name: string
    fiserv_mid: string | null
    volume: number
    transaction_count: number
  }>
  meta: {
    source: string
    scope: string
    generated_at: string
  }
}

function useDashboardMetrics() {
  return useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: async () => {
      const response = await api.get<DashboardMetrics>('/dashboard/metrics')
      return response.data
    },
    staleTime: 1000 * 60,
  })
}

function useTodayAppointments() {
  return useQuery({
    queryKey: ['dashboard-appointments-today'],
    queryFn: () => {
      const start = new Date()
      start.setHours(0, 0, 0, 0)
      const end = new Date(start)
      end.setDate(end.getDate() + 1)
      return appointmentsApi.listMine({
        from: start.toISOString(),
        to: end.toISOString(),
      })
    },
    staleTime: 1000 * 60,
  })
}

function LoadingValue() {
  return <span className="inline-block h-8 w-16 animate-pulse rounded-md bg-slate-100" aria-label="Loading" />
}

/**
 * Dashboard page — operational queue plus local Fiserv Reporting sync metrics.
 */
export function DashboardPage() {
  const { user } = useAuth()
  const { canViewDataHealth, hasRole } = usePermissions()
  const canOpenAppointments = hasRole([...APPOINTMENT_NAV_ROLES])
  const ownPipelineOnly = hasRole('sales_rep')
  const [dashboardSearch, setDashboardSearch] = useState('')
  const {
    data: metrics,
    isError: metricsFailed,
    isPending: metricsPending,
    refetch: refetchMetrics,
  } = useDashboardMetrics()
  const chartStatus = metricsFailed ? 'error' : metricsPending ? 'loading' : 'ready'
  const todayAppointments = useTodayAppointments()
  const { data: recentQueue, isLoading: recentLoading } = useLeads({
    per_page: 8,
    sort_by: 'updated_at',
    sort_dir: 'desc',
  })

  const counts = metrics?.lead_counts
  const portfolio = metrics?.portfolio
  const totalLeads = counts?.total ?? null
  const newCount = counts?.new_business ?? null
  const esignCount = counts?.esign ?? null
  const underwritingCount = counts?.underwriting ?? null
  const buildCount = counts?.build ?? null
  const liveCount = counts?.live ?? null
  const attentionCount = counts
    ? counts.client_returned + counts.conditional_approval + counts.pended + counts.validifi_flagged
    : null
  const verifiedCount = counts?.verification ?? null
  const showDataHealth = canViewDataHealth()
  const todayCount = todayAppointments.isSuccess
    ? todayAppointments.data.filter((appointment) => appointment.status === 'scheduled').length
    : null
  const appointmentsLabel = hasRole(['management', 'operations', 'super_admin'])
    ? "Today's appointments (all reps)"
    : "Today's appointments"

  const reportTabs = useMemo((): Array<{
    label: string
    value: number | null
    href?: string
    className?: string
  }> => [
    { label: 'Received', value: totalLeads, href: '/leads', className: 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300' },
    { label: 'New', value: newCount, href: '/leads?stage=new_business', className: 'border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300' },
    // eSign, underwriting, and build each add two stages. One stage filter cannot match the count.
    { label: 'eSign', value: esignCount },
    { label: 'Verification', value: verifiedCount, href: '/leads?stage=verification', className: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300' },
    { label: 'Underwriting', value: underwritingCount },
    { label: 'Build', value: buildCount },
    { label: 'Live', value: liveCount, href: '/leads?stage=live', className: 'border-emerald-300 bg-emerald-100 font-bold text-emerald-800 hover:border-emerald-400' },
  ], [buildCount, esignCount, liveCount, newCount, totalLeads, underwritingCount, verifiedCount])

  const attentionItems = [
    { label: 'Client Returned', value: counts?.client_returned, href: '/leads?stage=client_returned' },
    { label: 'Conditional', value: counts?.conditional_approval, href: '/leads?stage=conditional_approval' },
    { label: 'Pended', value: counts?.pended, href: '/leads?stage=pended' },
    { label: 'ValidiFI', value: counts?.validifi_flagged, href: '/leads?validifi_flagged=true' },
  ]

  const portfolioCards: Array<{
    title: string
    value: string
    subtitle: string
    tone: MiniStatTone
    href: string
  }> = [
    {
      title: 'MTD Volume',
      value: formatCurrency(portfolio?.mtd_volume) ?? '...',
      subtitle: `${portfolio?.mtd_transaction_count ?? '...'} sales; volume rounded to whole dollars`,
      tone: 'emerald',
      href: '/merchants?sort_by=monthly_volume',
    },
    {
      title: 'YTD Volume',
      value: formatCurrency(portfolio?.ytd_volume) ?? '...',
      subtitle: 'Year-to-date processing volume, rounded to whole dollars',
      tone: 'slate',
      href: '/merchants?sort_by=ytd_volume',
    },
    {
      title: 'Active Merchants',
      value: portfolio?.active_merchants?.toLocaleString() ?? '...',
      subtitle: `${portfolio?.first_batches_this_week ?? '...'} first batches this week`,
      tone: 'blue',
      href: '/merchants?status=active',
    },
    {
      title: 'Chargeback Ratio',
      value: portfolio ? `${(portfolio.chargeback_ratio * 100).toFixed(2)}%` : '...',
      subtitle: `${portfolio?.open_chargebacks ?? '...'} open cases`,
      tone: 'rose',
      href: '/merchants?has_open_chargebacks=true',
    },
    {
      title: 'Refunds MTD',
      value: formatCurrency(portfolio?.mtd_refunds) ?? '...',
      subtitle: `${formatCurrency(portfolio?.mtd_chargeback_amount) ?? '...'} Chargeback amount`,
      tone: 'amber',
      href: '/merchants',
    },
    {
      title: 'No batch in 7 days',
      value: portfolio?.no_batch_7_days?.toLocaleString() ?? '...',
      subtitle: 'Active accounts to check',
      tone: 'orange',
      href: '/merchants?no_batch_7_days=true',
    },
    ...(showDataHealth ? [
      {
      title: 'Data Delayed',
      value: portfolio?.sync_stale?.toLocaleString() ?? '...',
      subtitle: 'Reporting data not current',
      tone: 'amber' as const,
      href: '/merchants?sync_stale=true',
      },
      {
        title: 'Data Unavailable',
        value: portfolio?.sync_failed?.toLocaleString() ?? '...',
        subtitle: 'Background alert created',
        tone: 'rose' as const,
        href: '/merchants?sync_failed=true',
      },
    ] : []),
  ]

  const visibleQueue = useMemo(() => {
    const leads = recentQueue?.data ?? []
    const search = dashboardSearch.trim().toLowerCase()
    if (!search) return leads

    return leads.filter((lead) => [
      lead.dba_name,
      lead.legal_name,
      lead.contact_name,
      lead.business_email,
      lead.application?.fiserv_mid,
      lead.application?.fiserv_ext_ref_id,
    ].some((value) => value?.toLowerCase().includes(search)))
  }, [dashboardSearch, recentQueue?.data])

  function formatDate(date: string): string {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  function stageLabel(lead: Lead): string {
    return PIPELINE_STAGES[lead.stage as keyof typeof PIPELINE_STAGES]?.label ?? lead.stage
  }

  const appointmentsCard = (
    <StatCard
      label={appointmentsLabel}
      value={todayAppointments.isError ? '—' : todayCount === null ? <LoadingValue /> : todayCount}
      icon={Calendar}
      iconColor="teal"
      period="Today"
      context={todayAppointments.isError ? 'Unavailable' : 'Scheduled'}
      className="h-full"
    />
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        subtitle={`Welcome back, ${user?.name}`}
        action={(
          <>
          <Link to="/leads/new">
            <Button>New lead</Button>
          </Link>
          <Link to="/leads">
            <Button variant="secondary">Open Leads</Button>
          </Link>
          </>
        )}
      />

      <KnownIssueBanner />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Total Leads"
          value={totalLeads === null ? <LoadingValue /> : totalLeads}
          icon={Users}
          iconColor="blue"
          period="All time"
          context="All stages"
        />
        <StatCard
          label="New Business"
          value={newCount === null ? <LoadingValue /> : newCount}
          icon={Inbox}
          iconColor="emerald"
          period="Queue"
          context="Awaiting action"
        />
        <StatCard
          label="In eSign"
          value={esignCount === null ? <LoadingValue /> : esignCount}
          icon={FileSignature}
          iconColor="purple"
          period="Active"
          context="Sent or completed"
        />
        <StatCard
          label="Needs Attention"
          value={attentionCount === null ? <LoadingValue /> : attentionCount}
          icon={AlertTriangle}
          iconColor={attentionCount && attentionCount > 0 ? 'rose' : 'amber'}
          period="Review"
          context="Pended · Conditional · Returned · ValidiFI"
        />
        {canOpenAppointments ? (
          <Link to="/appointments" className="block rounded-2xl focus:outline-none focus:ring-2 focus:ring-brand-500">
            {appointmentsCard}
          </Link>
        ) : appointmentsCard}
      </div>

      <div className={`grid gap-4 ${ownPipelineOnly ? '' : 'lg:grid-cols-2'}`}>
        <SurfaceCard>
          <CardTitle>Pipeline</CardTitle>
          <p className="mt-1 text-sm text-muted">
            {ownPipelineOnly ? 'Your lead counts by stage' : 'Lead counts by stage'}
          </p>
          <div className="mt-5">
            <MetricBars
              items={chartStatus === 'ready' ? pipelineBars(counts) : null}
              status={chartStatus}
              onRetry={() => { void refetchMetrics() }}
              label="Pipeline stage counts"
            />
          </div>
        </SurfaceCard>
        {ownPipelineOnly ? null : (
          <SurfaceCard>
            <CardTitle>Portfolio</CardTitle>
            <p className="mt-1 text-sm text-muted">Merchant and case counts</p>
            <div className="mt-5">
              <MetricBars
                items={chartStatus === 'ready' ? portfolioBars(portfolio ?? undefined) : null}
                status={chartStatus}
                onRetry={() => { void refetchMetrics() }}
                label="Portfolio counts"
              />
            </div>
          </SurfaceCard>
        )}
      </div>

      {ownPipelineOnly ? null : (
      <SurfaceCard>
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Portfolio Pulse</CardTitle>
              <p className="mt-1 text-sm text-slate-500">
                Reporting data updated · {metrics ? new Date(metrics.meta.generated_at).toLocaleTimeString('en-US', {
                  hour: 'numeric',
                  minute: '2-digit',
                }) : 'Loading'}
              </p>
              <p className="mt-1 text-sm text-slate-500">
                Refunds, chargebacks, and top-merchant amounts are whole dollars.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-5">
          <div className={`grid grid-cols-1 gap-3 md:grid-cols-4 ${showDataHealth ? 'xl:grid-cols-8' : 'xl:grid-cols-6'}`}>
            {portfolioCards.map((card) => (
              <MiniStat
                key={card.title}
                label={card.title}
                value={card.value}
                context={card.subtitle}
                tone={card.tone}
                href={card.href}
              />
            ))}
          </div>
        </div>
      </SurfaceCard>
      )}

      <SurfaceCard>
        <CardTitle>Application Progress</CardTitle>
        <div className="mt-5">
          <div className="space-y-5">
            <div className="rounded-md border border-slate-200 bg-slate-50/80 p-3">
              <label htmlFor="dashboard-row-filter" className="mb-1 block text-sm font-medium text-slate-700">
                Filter the rows below
              </label>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <Input
                    id="dashboard-row-filter"
                    type="search"
                    placeholder="DBA, contact, MID, or Ext Ref"
                    value={dashboardSearch}
                    onChange={(event) => setDashboardSearch(event.target.value)}
                    className="min-w-0"
                  />
                </div>
                {dashboardSearch && (
                  <Button variant="ghost" className="shrink-0" onClick={() => setDashboardSearch('')}>
                    Clear
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 md:grid-cols-7">
              {reportTabs.map((tab) => {
                const body = (
                  <>
                    <div className="text-xl font-semibold">
                      {tab.value === null ? '...' : tab.value}
                    </div>
                    <div className="text-xs text-slate-500">{tab.label}</div>
                  </>
                )
                const className = `rounded-xl border p-3 text-center shadow-sm transition ${tab.className ?? ''}`

                if (!tab.href) {
                  return (
                    <div key={tab.label} className="px-3 py-3 text-center">
                      {body}
                    </div>
                  )
                }

                return (
                  <Link key={tab.label} to={tab.href} className={className}>
                    {body}
                  </Link>
                )
              })}
            </div>

            <div className="grid gap-4 lg:grid-cols-[1fr,280px]">
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-100 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">DBA</th>
                      <th className="px-4 py-2 text-left font-medium">Stage</th>
                      <th className="px-4 py-2 text-left font-medium">MID</th>
                      <th className="px-4 py-2 text-left font-medium">Volume</th>
                      <th className="px-4 py-2 text-left font-medium">Source</th>
                      <th className="px-4 py-2 text-left font-medium">Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {recentLoading ? (
                      <tr>
                        <td className="px-4 py-6 text-center text-gray-500" colSpan={6}>
                          Loading recent applications...
                        </td>
                      </tr>
                    ) : visibleQueue.length === 0 ? (
                      <tr>
                        <td className="px-4 py-6 text-center text-gray-500" colSpan={6}>
                          No recent applications match this filter.
                        </td>
                      </tr>
                    ) : (
                      visibleQueue.map((lead) => (
                        <tr key={lead.id} className="hover:bg-slate-50">
                          <td className="px-4 py-3">
                            <Link to={`/leads/${lead.id}`} className="font-medium text-ink hover:text-brand-700">
                              {lead.dba_name || lead.legal_name || 'Unnamed Lead'}
                            </Link>
                            <div className="text-xs text-gray-500">{lead.contact_name || lead.business_email || ''}</div>
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {isPipelineStage(lead.stage) ? (
                              <PipelineBadge stage={lead.stage} size="sm" />
                            ) : stageLabel(lead)}
                          </td>
                          <td className="px-4 py-3 font-mono text-xs text-gray-700">
                            {lead.application?.fiserv_mid || '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-700">
                            {formatCurrency(lead.monthly_volume) ?? '-'}
                          </td>
                          <td className="px-4 py-3 text-gray-500">{formatSource(lead.lead_source) || '-'}</td>
                          <td className="px-4 py-3 text-gray-500">{formatDate(lead.updated_at)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="space-y-4">
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
                  <div className="text-sm font-semibold text-amber-950">Needs Attention</div>
                  <div className="mt-3 space-y-2">
                    {attentionItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.href}
                        className="flex items-center justify-between rounded bg-white/70 px-3 py-2 text-sm hover:bg-white"
                      >
                        <span className="text-gray-700">{item.label}</span>
                        <span className="font-semibold text-amber-800">
                          {item.value === undefined ? '...' : item.value}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>

                {ownPipelineOnly ? null : (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                  <div className="text-sm font-semibold text-emerald-950">Top MTD Merchants</div>
                  <div className="mt-3 space-y-2">
                    {(metrics?.top_merchants ?? []).length === 0 ? (
                      <div className="rounded bg-white/70 px-3 py-3 text-sm text-gray-500">
                        No synced sales yet this month.
                      </div>
                    ) : (
                      metrics?.top_merchants.map((merchant) => (
                        <Link
                          key={merchant.id}
                          to={`/merchants/${merchant.id}`}
                          className="block rounded bg-white/70 px-3 py-2 text-sm hover:bg-white"
                        >
                          <div className="flex items-center justify-between gap-3">
                            <span className="truncate font-medium text-gray-800">{merchant.dba_name}</span>
                            <span className="font-semibold text-emerald-800">{formatCurrency(merchant.volume)}</span>
                          </div>
                          <div className="mt-1 flex items-center justify-between text-xs text-gray-500">
                            {merchant.fiserv_mid ? <span className="font-mono">{merchant.fiserv_mid}</span> : <span />}
                            <span>{merchant.transaction_count} sales</span>
                          </div>
                        </Link>
                      ))
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </SurfaceCard>
    </div>
  )
}
